import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { supabase } from '../supabase/client';
import { processCalendarData, addPredictions } from '../data/cycle-predictions/processCalendarData';

const TAG = '[useUpdateOvulationFlags]';

/**
 * Hook to update ovulation_actual and ovulation_prediction flags in the database.
 *
 * This hook watches the predictionUpdateCounter in Redux. When the counter is > 0
 * (meaning period days or ovulation tests changed), it:
 * 1. Processes the calendar data to compute predictions
 * 2. Compares with existing DB predictions
 * 3. Updates the DB if predictions changed
 *
 * The counter starts at 0, so the initial useEffect returns immediately without running.
 * Only actual user actions (period toggle, ovulation test update) trigger the counter increment.
 */
export default function useUpdateOvulationFlags() {
  const userId = useSelector((s) => s.user.profile?.id);
  const reduxCalendar = useSelector((s) => s.calendarDataSelf.data);
  const predictionUpdateCounter = useSelector((s) => s.calendarDataSelf.predictionUpdateCounter);

  useEffect(() => {
    // Skip on initial load (counter is 0)
    if (predictionUpdateCounter === 0) {
      return;
    }

    if (!userId || !reduxCalendar) {
      return;
    }

    const updateFlagsInDB = async () => {
      try {

        // Process calendar data to compute predictions
        const processed = processCalendarData(reduxCalendar);
        const withPredictions = addPredictions(processed);

        // Collect days with ovulation flags
        const ovulationActualDays = [];
        const ovulationPredictionDays = [];

        for (const [dateStr, dayData] of Object.entries(withPredictions)) {
          if (dayData.ipData?.ovulationActual) {
            ovulationActualDays.push(dateStr);
          }
          if (dayData.ipData?.ovulationPrediction) {
            ovulationPredictionDays.push(dateStr);
          }
        }

        // Query DB for existing flags
        const { data: existingFlags, error: fetchError } = await supabase
          .from('calendar_days')
          .select('day, ovulation_actual, ovulation_prediction')
          .eq('user_id', userId)
          .or('ovulation_actual.eq.true,ovulation_prediction.eq.true');

        if (fetchError) {
          return;
        }

        // Determine which days need updates
        const existingActualDays = new Set(
          existingFlags?.filter(f => f.ovulation_actual).map(f => f.day) || []
        );
        const existingPredictionDays = new Set(
          existingFlags?.filter(f => f.ovulation_prediction).map(f => f.day) || []
        );

        const daysToUpdate = new Set();

        // Days that need ovulation_actual set to true
        ovulationActualDays.forEach(day => {
          if (!existingActualDays.has(day)) {
            daysToUpdate.add({ day, ovulation_actual: true, ovulation_prediction: existingPredictionDays.has(day) });
          }
        });

        // Days that need ovulation_prediction set to true
        ovulationPredictionDays.forEach(day => {
          if (!existingPredictionDays.has(day)) {
            daysToUpdate.add({ day, ovulation_actual: existingActualDays.has(day), ovulation_prediction: true });
          }
        });

        // Days that need flags cleared (were true, now false)
        existingActualDays.forEach(day => {
          if (!ovulationActualDays.includes(day)) {
            daysToUpdate.add({ day, ovulation_actual: false, ovulation_prediction: existingPredictionDays.has(day) });
          }
        });

        existingPredictionDays.forEach(day => {
          if (!ovulationPredictionDays.includes(day)) {
            daysToUpdate.add({ day, ovulation_actual: existingActualDays.has(day), ovulation_prediction: false });
          }
        });

        if (daysToUpdate.size === 0) {
          return;
        }


        // Batch update all days
        const updates = Array.from(daysToUpdate).map(update =>
          supabase
            .from('calendar_days')
            .upsert({
              user_id: userId,
              day: update.day,
              ovulation_actual: update.ovulation_actual,
              ovulation_prediction: update.ovulation_prediction,
            }, { onConflict: 'user_id,day' })
        );

        await Promise.all(updates);

      } catch (error) {
      }
    };

    // Run async update without blocking
    updateFlagsInDB();
  }, [predictionUpdateCounter, userId, reduxCalendar]);
}
